import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { canViewGuestPii, useAuth } from '@/shared/auth';
import { Button } from '@/shared/components';
import { useApi, type BookingResponse, type GuestProfileResponse } from '@/shared/api';
import { BookingInvoiceGroup } from './BookingInvoiceGroup';
import { downloadInvoicePdf } from './downloadInvoicePdf';
import { LoadError, StaleNote } from './StaleNote';
import { InvoiceKindDialog } from './InvoiceKindDialog';
import { useBookingInvoices } from './useBookingInvoices';

interface Props {
  booking: BookingResponse;
  /** Nur fuer die Frage B2B oder nicht - der Versand selbst holt den Empfaenger serverseitig. */
  guest?: GuestProfileResponse | null;
}

export function InvoiceRow({ booking, guest }: Props) {
  const { user } = useAuth();
  const canManageDocuments = canViewGuestPii(user);
  const api = useApi();
  const navigate = useNavigate();
  const invoicing = useBookingInvoices(booking);
  const [kindDialogOpen, setKindDialogOpen] = useState(false);
  const canCreateAccommodationInvoice =
    booking.capabilities?.canCreateAccommodationInvoice === true;
  // Dieselbe Bedingung, die der Server prueft: abgeschlossenes Onboarding, Firmenbuchung, Firma
  // hinterlegt — und ein festgeschriebener Beleg, den man ueberhaupt anhaengen koennte. Ein Knopf,
  // der nur absagen kann, ist schlechter als keiner.
  const canSendMail =
    guest?.bookingType === 'B2B' &&
    guest.status === 'Final' &&
    guest.company !== null &&
    invoicing.hasIssuedInvoice;

  if (invoicing.isLoading) {
    return <p className="text-ink-soft text-sm">Rechnungen werden geladen …</p>;
  }
  // Ohne Liste keine Knöpfe: „Rechnung erzeugen“ neben einer nicht geladenen Liste lädt zum Doppeln ein.
  if (invoicing.loadError) {
    return (
      <LoadError
        error={invoicing.loadError}
        fallback="Die Rechnungen konnten nicht geladen werden."
        onRetry={invoicing.reload}
        retrying={invoicing.reloading}
      />
    );
  }

  return (
    <div>
      {invoicing.stale && <StaleNote onRetry={invoicing.reload} />}
      {invoicing.invoices.length === 0 ? (
        <p className="text-ink-soft text-sm">Noch keine Rechnung erzeugt.</p>
      ) : (
        <div className="space-y-4">
          {invoicing.invoices.map((invoice) => (
            <BookingInvoiceGroup
              key={invoice.id}
              invoice={invoice}
              cancelling={
                invoicing.cancel.isPending && invoicing.cancel.variables?.invoiceId === invoice.id
              }
              discarding={
                invoicing.discardDraft.isPending && invoicing.discardDraft.variables === invoice.id
              }
              onDownload={(id, fileName) => downloadInvoicePdf(api, id, fileName)}
              onCancel={(invoiceId, reason, done) =>
                invoicing.cancel.mutate({ invoiceId, reason }, { onSuccess: done })
              }
              onCorrectRecipient={(invoiceId) =>
                void navigate({
                  to: '/bookings/$id/rechnung/$invoiceId/empfaenger',
                  params: { id: booking.id, invoiceId },
                })
              }
              onOpenDraft={(draftId) =>
                void navigate({
                  to: '/bookings/$id/rechnung/$draftId',
                  params: { id: booking.id, draftId },
                })
              }
              onDiscardDraft={(draftId) => invoicing.discardDraft.mutate(draftId)}
            />
          ))}
        </div>
      )}

      {canManageDocuments && (
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {canCreateAccommodationInvoice && !invoicing.hasAccommodationInvoice && (
            <Button
              variant="brand"
              size="sm"
              onClick={() => invoicing.createAccommodation.mutate()}
              loading={invoicing.createAccommodation.isPending}
            >
              Rechnung erzeugen
            </Button>
          )}
          {/* Der Builder: eine Rechnung von Hand, neben den automatischen. Genau der Fall
            „Gast bucht den Parkplatz nachträglich" oder eine Schadensrechnung. */}
          <Button variant="ghost" size="sm" onClick={() => setKindDialogOpen(true)}>
            Beleg erstellen
          </Button>
          <InvoiceKindDialog
            open={kindDialogOpen}
            pending={invoicing.createDraft.isPending}
            onOpenChange={setKindDialogOpen}
            onCreate={(kind) =>
              invoicing.createDraft.mutate(kind, {
                onSuccess: (draft) =>
                  void navigate({
                    to: '/bookings/$id/rechnung/$draftId',
                    params: { id: booking.id, draftId: draft.id },
                  }),
              })
            }
          />
          {canSendMail && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => invoicing.resendMail.mutate()}
              loading={invoicing.resendMail.isPending}
            >
              Rechnungen per E-Mail senden
            </Button>
          )}
          {/* Nur anbieten, wenn ein Parkplatz gebucht ist und keine Rechnung dazu existiert —
            sonst wäre der Knopf ein Angebot, das ins Leere läuft. */}
          {invoicing.parkingInvoiceMissing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => invoicing.createParking.mutate()}
              loading={invoicing.createParking.isPending}
            >
              Parkplatzrechnung erzeugen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
